import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useMemo, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { formatDuration } from '../date-utils';
import { useSelector } from '../hook-utils';
import { AgentLimt, CallState, CallType, Extension, ExtensionDevice, Identifier, Media, MediaType } from '../model';
import { getActiveDevice } from '../store/reducers';
import { MyDevices, selectConfigProp, selectDateFormating, selectMyAccess, selectMyDevices, selectMyRegion, selectMyself } from '../store/reducers/config';
import { selectEmail } from '../store/reducers/emails';
import { getNow } from '../store/reducers/now';
import { selectExtension } from '../store/reducers/queueAgents';
import { Except } from '../type-utils';
import { agentStatusTitle, formatRelativeDateTime } from './AgentsTable';
import { callClidDescription, callStateDescription, getCallStatus } from './CallPopup';
import CallStatusIcon from './CallStatusIcon';
import { useCompactView } from './compactView';
import { ReactComponent as WrapupIcon } from './icons/callstatus/wrapup.svg';
import MediaIcon, { MediaItemIconType } from './MediaIcon';
import './MediaItem.scss';
import Timer from './Timer';
import TitleTag from './TitleTag';

interface PropsMediaItem extends React.HTMLAttributes<HTMLDivElement> {
  item: Media
  className: string,
  icon?: MediaItemIconType,
  onMenu?(e: React.MouseEvent): void,
  innerRef?: React.Ref<HTMLDivElement>
}

interface PropsDrawMediaItem extends React.HTMLAttributes<HTMLDivElement> {
  myId: Identifier,
  myExtension: Extension,
  item: Media,
  className: string,
  icon?: MediaItemIconType,
  onMenu?(e: React.MouseEvent): void,
  innerRef?: React.Ref<HTMLDivElement>,
  devices: MyDevices,
  agentLimits: AgentLimt[],
  shortTimeFormat: string,
  shortDateFormat: string,
  compactView: boolean,
}

const Classes: Partial<Record<MediaType, string>> = {
  'CALL': 'media-call',
  'EMAIL': 'media-email',
  'FB': 'media-facebook',
  'OUT': 'media-outbound',
  'TC': 'media-chat',
  'TWEET': 'media-tweet',
}

export function MediaItem({ item, className, icon, innerRef, onMenu, ...rest }: PropsMediaItem) {
  const compactView = useCompactView()
  const myId = useSelector(selectMyself)
  const extension = useSelector((s) => myId && selectExtension(s, myId))
  const devices = useSelector(selectMyDevices)
  const agentLimits = useSelector(s => selectConfigProp(s, 'agentLimits'));
  const format = useSelector(selectDateFormating)

  if (!myId || !devices || !extension || !agentLimits || !format) { return <div ref={innerRef} {...rest} /> }

  return <DrawMediaItem myId={myId} myExtension={extension} item={item} className={className} devices={devices} agentLimits={agentLimits} shortTimeFormat={format.shortTimeFormat} shortDateFormat={format.shortDateFormat} compactView={compactView} icon={icon} onMenu={onMenu} innerRef={innerRef} {...rest} />
}

function DrawMediaItem({ myId, myExtension, item, className, devices, agentLimits, shortTimeFormat, shortDateFormat, compactView, icon, onMenu, innerRef, ...rest }: PropsDrawMediaItem) {
  const now = useSelector(getNow)
  const device = getActiveDevice(item, devices.devices, devices.primaryDevice)
  const status = agentStatusTitle(now, agentLimits, [item])
  const ref = useRef<HTMLDivElement>(null)

  const media = useMemo(() => {
    function renderIcon(className: string, onClick?: (e: React.MouseEvent) => void, icon?: MediaItemIconType) {
      if (icon === MediaItemIconType.white) {
        return <MediaIcon className={className} mediaType={item.mediaType} callStatus={item.status} iconType={icon} onClick={onClick} title={false} />
      } else {
        switch (item.mediaType) {
          case 'CALL':
            return <CallStatusIcon className={className} callStatus={item.status} callType={item.callType} onClick={onClick} title={false} />
          default:
            return <MediaIcon className={className} mediaType={item.mediaType} callStatus={item.status} onClick={onClick} title={false} />
        }
      }
    }

    return <div ref={ref}>
      <div ref={innerRef} className={classNames(className, compactView && 'compact-view', status !== '' && 'warning')}
        onClick={(e) => {

          e.preventDefault()
          e.stopPropagation()

          onMenu && onMenu(e)
        }} {...rest}>
        {renderIcon(Classes[item.mediaType] || Classes['CALL']!, onMenu, icon)}
        <div className='name'><CallerName media={item} /></div>
        <div className='details'>
          {item.status !== CallState.None && <Timer ts={item.elapsedTime} />}
          {<>
            {item.status !== CallState.None && ' | '}
            <span>{getCallStatus(item.status) + (item.status === CallState.QueueHold && item.preferredExtnDescription ? ' for ' + item.preferredExtnDescription : '')}</span>
          </>}
        </div>
        <div className='wrapup'>{item.status === CallState.WaitingForWrapup && <WrapupIcon />}</div>
      </div >
    </div>
  }, [className, compactView, icon, innerRef, item, onMenu, ref, rest, status])

  return <>
    {media}
    <TitleTag targetRef={ref.current}><MediaItemIconTitle myId={myId} media={item} device={device} status={status} /></TitleTag>
  </>
}

function CallerName({ media }: { media: Media }) {
  const region = useSelector(selectMyRegion)
  const email = useSelector(s => selectEmail(s, media.id))

  if (media.mediaType === 'EMAIL' && media.callType === CallType.Outgoing) {
    return <>{email && email.reply && email.reply.to || 'New email'}</>
  } else {
    return <>{callClidDescription(media, region)}</>
  }
}

interface DraggableProps extends Except<PropsMediaItem, 'innerRef'> {
  index: number
}

export function DraggableMediaItem({ index, item, ...rest }: DraggableProps) {
  const access = useSelector(selectMyAccess)
  const canRetrieve = !access.isLyncLicensed

  return <Draggable isDragDisabled={!canRetrieve} draggableId={item.id} index={index} key={index}>
    {(provided) => <MediaItem item={item} {...rest} innerRef={provided.innerRef} {...provided.draggableProps}
      style={{ ...provided.draggableProps.style, transition: '1ms' }} {...provided.dragHandleProps} />}
  </Draggable>
}

export function MediaItemIconTitle({ myId, media, device, status }: { myId: Identifier, media: Media, device: ExtensionDevice | undefined, status: string | undefined }) {
  const title = callStateDescription(media, myId, device)
  const s = useMemo(() => {
    return <div>{status}</div>
  }, [status])
  const t = useMemo(() => {
    return <div>{title}</div>
  }, [title])

  return <>
    {t}
    {media.isQueueCall && <MediaItemQueueIconTitle media={media} />}
    {s}
  </>
}

export function MediaItemQueueIconTitle({ media }: { media: Media }) {
  const format = useSelector(selectDateFormating)
  const now = Date.now()
  const start = dayjs(now - (media.queuingTime + media.heldTime + media.talkTime + media.wrapupTime) * 1000)

  if (!format) return null

  return <div>
    <div>Start Date: {formatRelativeDateTime(start.valueOf(), format.shortDateFormat)}</div>
    <div className={'bottom'}>Start Time: {start.format(format.shortTimeFormat)}</div>
    <div>Queuing Time: {formatDuration(media.status === CallState.Queueing || media.status === CallState.AnnouncementAnswered || media.status === CallState.AnnouncementPending ? Math.floor((now - media.elapsedTime) / 1000) : media.queuingTime)}</div>
    <div>Wrapup Time: {formatDuration(media.status === CallState.WaitingForWrapup ? Math.floor((now - media.elapsedTime) / 1000) : media.wrapupTime)}</div>
    <div>Held Time: {formatDuration(media.status === CallState.QueueHold || media.status === CallState.Hold ? Math.floor((now - media.elapsedTime) / 1000) : media.heldTime)}</div>
    <div>Talk Time: {formatDuration(media.status === CallState.ExtensionAnswered || media.status === CallState.ExtensionTransfer ? Math.floor((now - media.elapsedTime) / 1000) : media.talkTime)}</div>
  </div >
}
